import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Send, MessageSquare, ChevronLeft, LifeBuoy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function SupportTickets({ open, onClose }) {
  const { t } = useI18n();
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  const loadList = async () => {
    try { const { data } = await api.get("/tickets"); setTickets(data); }
    catch { /* ignore */ }
  };

  useEffect(() => { if (open) { setView("list"); loadList(); } }, [open]);

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setActive(data); setView("thread");
    } catch { toast.error(t("err_generic")); }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/tickets", { subject, message });
      setSubject(""); setMessage("");
      toast.success(t("ticket_sent"));
      await loadList();
      setView("list");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setLoading(true);
    try {
      await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setReply("");
      await openTicket(active.id);
    } catch { toast.error(t("err_generic")); }
    finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col" data-testid="support-sheet">
        <SheetHeader className="p-5 border-b border-white/5">
          <SheetTitle className="text-white font-display text-xl font-extrabold flex items-center gap-2">
            <LifeBuoy className="text-[#00E676]" size={20} /> {t("tickets_title")}
          </SheetTitle>
        </SheetHeader>

        {view === "list" && (
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            <p className="text-white/40 text-sm">{t("tickets_desc")}</p>
            <Button data-testid="ticket-new-btn" onClick={() => setView("new")} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-11 font-semibold">
              <Plus size={16} className="mr-1.5" /> {t("ticket_new")}
            </Button>
            {tickets.length === 0 ? (
              <div className="text-white/30 text-sm text-center py-10" data-testid="tickets-empty">{t("ticket_empty")}</div>
            ) : (
              tickets.map((tk) => (
                <button key={tk.id} data-testid={`ticket-item-${tk.id}`} onClick={() => openTicket(tk.id)}
                  className="w-full text-left gt-card p-4 hover:border-white/20 transition-all">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">{tk.subject}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${tk.status === "open" ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/10 text-white/50"}`}>
                      {tk.status === "open" ? t("ticket_open") : t("ticket_closed")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-white/40 text-xs">
                    <MessageSquare size={12} /> {tk.messages?.length || 0}
                    {tk.user_unread && <span className="w-2 h-2 rounded-full bg-[#FF3B30] inline-block" data-testid="ticket-unread-dot" />}
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {view === "new" && (
          <form onSubmit={createTicket} className="flex-1 overflow-y-auto p-5 space-y-3">
            <button type="button" onClick={() => setView("list")} className="text-white/50 hover:text-white text-sm flex items-center gap-1">
              <ChevronLeft size={14} /> {t("ticket_back")}
            </button>
            <Input data-testid="ticket-subject-input" className="bg-[#111111] border-white/10 text-white rounded-[12px] h-11" placeholder={t("ticket_subject")} value={subject} onChange={(e) => setSubject(e.target.value)} required />
            <Textarea data-testid="ticket-message-input" className="bg-[#111111] border-white/10 text-white rounded-[12px] min-h-[120px]" placeholder={t("ticket_message")} value={message} onChange={(e) => setMessage(e.target.value)} required />
            <Button data-testid="ticket-create-submit" type="submit" disabled={loading} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-11 font-semibold">
              {loading ? <Loader2 className="animate-spin" /> : t("ticket_send")}
            </Button>
          </form>
        )}

        {view === "thread" && active && (
          <>
            <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
              <button type="button" onClick={() => { setView("list"); loadList(); }} data-testid="ticket-back-btn" className="text-white/50 hover:text-white">
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold truncate">{active.subject}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="ticket-thread">
              {active.messages.map((m, i) => (
                <div key={i} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-[14px] px-3.5 py-2.5 text-sm ${m.sender === "user" ? "bg-[#007BFF] text-white" : "bg-[#151515] border border-white/10 text-white/90"}`}>
                    <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">{m.sender === "user" ? t("ticket_you") : t("ticket_team")}</div>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
            {active.status === "open" && (
              <form onSubmit={sendReply} className="p-4 border-t border-white/5 flex gap-2">
                <Input data-testid="ticket-reply-input" className="bg-[#111111] border-white/10 text-white rounded-[12px] h-11" placeholder={t("ticket_reply_ph")} value={reply} onChange={(e) => setReply(e.target.value)} />
                <Button data-testid="ticket-reply-submit" type="submit" disabled={loading} className="bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-11 px-4">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </Button>
              </form>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
